import { ReadonlyMat3, ReadonlyVec2, vec2 } from 'gl-matrix';
import { DicomProcessor } from '../../domain/services/dicomProcessor';
import { KeyedListeners } from '../core/KeyedListeners';
import { RENDERING_CONTEXT_ATTRIBUTES } from '../core/render/renderingContextAttributes';
import type { RigidRegistration } from '~/domain/patient/rigidRegistration';
import type { Environment } from '../data/Environment';
import { ModifierKeys } from '../tool/ModifierKeys';
import { buttonsMaskOf } from '../tool/MouseButton';
import { InteractionContext } from '../tool/InteractionContext';
import type { ToolManager } from '../tool/ToolManager';
import { LayerBit } from './layers/LayerBit';
import { LayerBitMask } from './layers/Layer';
import { EnvironmentFrameRotations } from './EnvironmentFrameRotations';
import type { EnvironmentViews } from './EnvironmentViews';
import { ViewInvalidationScheduler } from './ViewInvalidationScheduler';
import {
    InvalidationVolume,
    resolveInvalidationVolume,
    shouldUpdateView,
    ViewInvalidator,
} from './ViewInvalidator';
import { PlaneView } from './views/PlaneView';
import type { TextureView } from './TextureView';
import { isTextEntryElement } from '~/utils/textEntry';
import { isExcludedFromViewInput } from '~/utils/viewInputExclusion';

/**
 * Class that manages all the {@link TextureView Views}, their positions and scales.
 */
export class ViewManager
    implements EventListenerObject, EventTarget, ViewInvalidator
{
    /**
     * The {@link ViewManager} singleton.
     */
    private static manager: ViewManager | undefined = undefined;

    /**
     * The tool state this viewer's input is dispatched to. Every input
     * surface attached with {@link attachInputSurface}, and the document
     * keys, reach the tools through it.
     */
    public readonly interactionContext = new InteractionContext();

    /** The tools of {@link interactionContext}. */
    private get toolManager(): ToolManager {
        return this.interactionContext.toolManager;
    }

    /**
     * App-global engine event hub. Its identity outlives every input
     * surface, so subscribers survive viewport remounts and dispatch works
     * before any surface is attached.
     */
    private readonly eventHub = new EventTarget();

    /**
     * The {@link OffscreenCanvas} that provides us the {@link WebGL2RenderingContext WebGL context}.\
     * This context is created within {@link initializeViewManager}-method\
     * and stored inside the global {@link gl}-variable.
     */
    private readonly canvas: OffscreenCanvas;

    /**
     * Currently active {@link TextureView}.\
     * This may be undefined if there's not currently active {@link TextureView}.
     */
    private _activeView: PlaneView | undefined;

    /** The view last pressed or wheeled on, which outlives the pointer leaving it. */
    private _lastUsedView: PlaneView | undefined;

    /** Buttons whose press on a view reached the tools, not yet released. */
    private readonly buttonsPressedOnView = new Set<number>();

    /**
     * Views owned by the N-tile grid, each drawing one per-dataset
     * Environment. A view is reset through its Environment
     * ({@link resetViewsForEnvironment}), never on its own.
     */
    private readonly _gridViews: Array<PlaneView> = [];

    /**
     * {@link viewsFor} subscribers, keyed by the Environment whose views
     * they follow, and {@link everyView} subscribers under null. A released
     * Environment holds nothing here once its last listener is gone.
     */
    private readonly viewListeners = new KeyedListeners<Environment | null>();

    /**
     * {@link subscribeFusedImageDisplay} subscribers, keyed by the
     * Environment whose views they follow.
     */
    private readonly fusedImageDisplayListeners =
        new KeyedListeners<Environment>();

    /**
     * Passes a held view's change of fused display on to the subscribers of
     * its Environment, then dispatches `fusedImageDisplayChanged` on the
     * event hub for listeners that follow every view.
     */
    private readonly onFusedImageDisplayChanged = (view: TextureView): void => {
        this.fusedImageDisplayListeners.notify(view.environment);
        this.dispatchEvent(new Event('fusedImageDisplayChanged'));
    };

    /** The oblique frame of each grid Environment's views, one per Environment. */
    private readonly frameRotations = new EnvironmentFrameRotations();

    /**
     * The live Environments, read on each use: none until
     * {@link setResidentEnvironments} wires a source.
     */
    private residentEnvironments: () => ReadonlyArray<Environment> = () => [];

    /**
     * The Environments whose camera a first grid view has framed
     * ({@link fitGridView}). Weak, so a released Environment leaves with
     * its last reference and a re-minted one starts unframed.
     */
    private readonly framedEnvironments = new WeakSet<Environment>();

    /**
     * Coalesces {@link invalidate} requests into one repaint per view per frame.
     * Reads {@link allViews} at flush time, so a view removed after it was marked
     * dirty is never repainted.
     *
     * The {@link ViewManager} singleton itself has no teardown path — it lives for
     * the lifetime of the document — so there is no place to cancel a pending frame
     * from. Views are torn down individually ({@link removeGridView}),
     * and each drops its dirty entry; the worst a frame scheduled just before the
     * last removal can do is run one empty flush.
     */
    private readonly invalidationScheduler = new ViewInvalidationScheduler(() =>
        this.allViews()
    );

    private constructor(dicomProcessor: DicomProcessor);

    private constructor(
        dicomProcessor: DicomProcessor,
        width: number,
        height: number
    );

    /**
     * Private constructor that will create {@link ViewManager}.\
     * With no size, it has no input surface of its own: each surface
     * attaches one with {@link attachInputSurface}, and only the document
     * keys ({@link listenToDocumentKeys}) are routed here.
     * @param width The initial canvas width.
     */
    private constructor(
        private readonly dicomProcessor: DicomProcessor,
        width?: number,
        height?: number
    ) {
        if (width === undefined) {
            this.canvas = new OffscreenCanvas(1, 1);
            this.listenToDocumentKeys();
        } else {
            this.canvas = new OffscreenCanvas(width, height as number);
        }
    }

    /**
     * Implementation of {@link EventTarget.addEventListener}.\
     * @param type
     * @param callback
     * @param options
     */
    public addEventListener(
        type: string,
        callback: EventListenerOrEventListenerObject,
        options?: boolean | AddEventListenerOptions | undefined
    ): void {
        this.eventHub.addEventListener(type, callback, options);
    }

    /**
     * Implementation of {@link EventTarget.dispatchEvent}.\
     * @param event
     */
    public dispatchEvent(event: Event): boolean {
        return this.eventHub.dispatchEvent(event);
    }

    /**
     * Implementation of {@link EventTarget.removeEventListener}.\
     * @param type
     * @param callback
     * @param options
     */
    public removeEventListener(
        type: string,
        callback: EventListenerOrEventListenerObject,
        options?: boolean | EventListenerOptions | undefined
    ): void {
        this.eventHub.removeEventListener(type, callback, options);
    }

    /**
     * Routes the document's keydown and keyup to the pan-key handling, and
     * the document's Escape keydown to a tool action in progress
     * ({@link cancelActionInProgress}). The DOM ignores a second
     * registration of the same listener, so a repeat call installs nothing
     * more.
     */
    private listenToDocumentKeys(): void {
        document.addEventListener('keydown', this.onDocumentKeyDown);
        document.addEventListener('keyup', this.onDocumentKeyUp);
    }

    /**
     * The DOM input events {@link handleEvent} consumes. Kept in one place so
     * every input surface receives the same vocabulary.
     */
    private static readonly INPUT_EVENTS = [
        'mousedown',
        'mousemove',
        'mouseup',
        'mouseenter',
        'mouseleave',
        'wheel',
        'dblclick',
        'contextmenu',
        'keydown',
        'keyup',
    ] as const;

    /** The input events an element excluded from view input may keep. */
    private static readonly EXCLUDABLE_EVENTS: ReadonlySet<string> = new Set([
        'mousedown',
        'mousemove',
        'mouseup',
        'wheel',
        'dblclick',
        'keydown',
    ]);

    /**
     * Route an element's DOM input events into this ViewManager (it is its
     * own EventListenerObject). It adds listeners to the element and removes
     * none, so other attached elements keep theirs.
     */
    public attachInputSurface(element: HTMLElement): void {
        element.tabIndex = -1;
        ViewManager.INPUT_EVENTS.forEach((type) =>
            element.addEventListener(type, this)
        );
    }

    /**
     * Stop routing an element's DOM input events, and forget the presses on
     * a view still held: their release may never arrive.
     */
    public detachInputSurface(element: HTMLElement): void {
        ViewManager.INPUT_EVENTS.forEach((type) =>
            element.removeEventListener(type, this)
        );
        this.buttonsPressedOnView.clear();
    }

    private onDocumentKeyDown = (event: KeyboardEvent): void => {
        if (isTextEntryElement(event.target)) {
            return;
        }
        if (this.cancelActionInProgress(event)) {
            event.preventDefault();
            return;
        }
        const consumed = this.toolManager.onPanKeyDown(event.key);
        if (consumed && this._activeView) {
            event.preventDefault();
        }
    };

    /**
     * Passes an Escape to the armed tool while it has an action in progress
     * (a line being drawn), wherever the keyboard focus and the pointer are.
     * Other keys reach the tools only from an input surface, so after a click
     * outside the viewports, for example on a toolbar tray, the Escape that
     * cancels the action would not reach it. The key goes with the view
     * {@link viewForKey} picks; with none, nothing is passed. An Escape that
     * an input surface already handled is left alone. Returns whether the
     * tool handled the key.
     */
    private cancelActionInProgress(event: KeyboardEvent): boolean {
        if (event.key !== 'Escape' || event.defaultPrevented) {
            return false;
        }
        if (!this.toolManager.isActiveToolActionInProgress()) {
            return false;
        }
        const view = this.viewForKey(event.key);
        if (!view) {
            return false;
        }
        return this.toolManager.onKeyPress(
            event.key,
            ModifierKeys.getModifierKeys(event),
            view
        );
    }

    /**
     * The view a key is passed to the tools with. An Escape goes with the
     * view the armed tool's action in progress is drawn in, so it cancels
     * that action wherever the pointer is; an Escape with no such view, and
     * every other key, goes with the view under the pointer. Undefined when
     * there is neither.
     */
    private viewForKey(key: string): PlaneView | undefined {
        if (key === 'Escape') {
            return (
                this.toolManager.getActiveToolDrawingView() ?? this._activeView
            );
        }
        return this._activeView;
    }

    private onDocumentKeyUp = (event: KeyboardEvent): void => {
        // Always clear pan key state (even in text inputs)
        // to prevent stuck state when a key was pressed
        // outside a text input and released inside one.
        const consumed = this.toolManager.onPanKeyUp(event.key);
        if (consumed && !isTextEntryElement(event.target)) {
            event.preventDefault();
        }
    };

    /**
     * Resets every Environment a grid view shows, and every turned resident
     * Environment no view shows at the moment, frame included, so every
     * row returns to the patient-aligned planes. Insertion order of the
     * grid views, then resident order, keeps this deterministic.
     */
    public resetViews() {
        const gridEnvironments = new Set([
            ...this._gridViews.map((view) => view.environment),
            ...this.residentEnvironments().filter((environment) =>
                this.frameRotations.isTurned(environment)
            ),
        ]);
        for (const environment of gridEnvironments) {
            this.resetViewsForEnvironment(environment);
        }
        // Every environment has now repainted synchronously with its own
        // reset. A later environment's reset re-dirties an earlier
        // environment's already-repainted views (its pan/zoom write is a
        // change the shared invalidator sees), which would otherwise
        // schedule a frame that repaints them a second time. Clearing the
        // scheduler once, after every environment is done, supersedes that.
        // Unlike resetViewsForEnvironment's per-view discard, this is safe
        // here because nothing outside this call's own environments has had
        // a chance to raise a legitimate pending repaint in between.
        this.invalidationScheduler.reset();
    }

    /**
     * Reset one Environment's frame, camera + cursor and repaint only that
     * Environment's views (matched by view.environment). Scoped so a per-dataset
     * bind or a setImage on one dataset never resets another dataset's tiles.
     */
    public resetViewsForEnvironment(env: Environment): void {
        this.clearFrameRotation(env);
        env.panZoom.scale = 1;
        if (env.image) {
            env.updatePlaneIntersectionPoint(env.image.centerVoxelCenter);
        }
        for (const view of this.allViews()) {
            if (view.environment !== env) {
                continue;
            }
            this.fitToImage(view);
        }
        for (const view of this.allViews()) {
            if (view.environment !== env) {
                continue;
            }
            view.updateView(LayerBit.ALL_LAYERS);
            // The repaint above covers ALL_LAYERS for this view, which
            // supersedes any mask accumulated before or during the reset —
            // including the invalidations the pan/zoom change above raised.
            // Discarding per view, rather than resetting the whole scheduler,
            // keeps this scoped to env's own views: another environment's
            // unrelated pending repaint must survive this call. The reset
            // itself is synchronous: callers rely on this environment's views
            // being current when it returns.
            this.invalidationScheduler.discard(view);
        }
    }

    /**
     * Fits `view` to its Environment's image at the Environment's zoom:
     * the fit is zoom 1, so the view's own scale restarts there before the
     * zoom is applied. The fit centres the view on the image's box, whose
     * centre lies between two slices for an even slice count, so the view
     * is put back on the slice through the Environment's cursor: the plane
     * it draws is the plane every readout and tool reads. Does nothing
     * while the Environment has no image.
     */
    private fitToImage(view: PlaneView): void {
        const env = view.environment;
        if (!env.image) {
            return;
        }
        view.fitToView(env.image.boundingBox);
        view.updateToOrthoPosition(env.planeIntersectionPoint.coordinates);
        view.resetCurrentScale(1);
        view.updateZoomScale(env.panZoom.scale);
    }

    /**
     * Fits a held grid view that now has its layout size. The first view
     * fitted on an Environment frames it: the Environment's views, frame,
     * zoom and cursor are reset ({@link resetViewsForEnvironment}). A view
     * fitted on a framed Environment is fitted alone: to the image, at the
     * Environment's zoom, on the slice through the Environment's cursor and
     * under the frame it was turned to on join. No other view moves and no
     * frame subscriber is told.
     */
    public fitGridView(view: PlaneView): void {
        const env = view.environment;
        if (!this.framedEnvironments.has(env)) {
            this.framedEnvironments.add(env);
            this.resetViewsForEnvironment(env);
            return;
        }
        this.fitToImage(view);
        view.updateView(LayerBit.ALL_LAYERS);
        this.invalidationScheduler.discard(view);
    }

    public static initializeViewManager(
        dp: DicomProcessor
    ): [ViewManager, WebGL2RenderingContext];

    public static initializeViewManager(
        dp: DicomProcessor,
        width: number,
        height: number
    ): [ViewManager, WebGL2RenderingContext];

    /**
     * Method to initialize the {@link ViewManager.manager ViewManager singleton}.\
     * This method will also {@link Canvas.getContext initialize} the {@link WebGL2RenderingContext}.
     * With no size, a later call returns the existing singleton unchanged.
     * @param width The width in pixels this {@link ViewManager} should have after initialization, or nothing.
     * @param height The height in pixels, given together with the width.
     * @returns A {@link WebGL2RenderingContext}, which is the WebGL context of the {@link canvas OffscreenCanvas}.
     */
    public static initializeViewManager(
        dp: DicomProcessor,
        width?: number,
        height?: number
    ): [ViewManager, WebGL2RenderingContext] {
        if (!ViewManager.manager) {
            ViewManager.manager =
                width === undefined
                    ? new ViewManager(dp)
                    : new ViewManager(dp, width, height as number);
        } else if (width !== undefined) {
            throw new Error(
                `Attempt to initialize ViewManager with size ${width}x${height}, while ViewManager has already been initialized`
            );
        }
        const context = ViewManager.manager.canvas.getContext(
            'webgl2',
            RENDERING_CONTEXT_ATTRIBUTES
        );
        if (!context) {
            throw new Error('WebGL 2 is not available');
        }
        return [ViewManager.manager, context as WebGL2RenderingContext];
    }

    /** Every view this manager holds: the grid tiles, in the order they joined. */
    private allViews(): readonly PlaneView[] {
        return this._gridViews;
    }

    /**
     * Adds a grid tile's view. A view joining an Environment that is turned
     * is turned to the same frame on join, so a re-hung or newly opened tile
     * shows what its siblings show, and a fused Environment re-transforms
     * its secondary image through its registration's current matrix. The
     * view is fitted once it has a size ({@link fitGridView}).
     */
    public addGridView(view: PlaneView) {
        this._gridViews.push(view);
        this.frameRotations.applyStoredTo(view);
        if (view.environment.registration) {
            view.environment.updateSecondaryTransformation();
        }
        view.fusedImageDisplayChanged.subscribe(
            this.onFusedImageDisplayChanged
        );
        this.notifyViewsChanged(view.environment);
    }

    /** The oblique frame of `environment`'s views; identity while patient-aligned. */
    public frameRotationOf(environment: Environment): ReadonlyMat3 {
        return this.frameRotations.frameOf(environment);
    }

    /**
     * The one writer of an Environment's frame: stores a copy of `rotation`,
     * turns every view of `environment` to it (each view keeps its own
     * anatomical base orientation under the shared frame, so the views stay
     * mutually perpendicular), then notifies the frame's subscribers. A
     * patient-aligned rotation returns the views to their base orientation.
     */
    public setFrameRotation(
        environment: Environment,
        rotation: ReadonlyMat3
    ): void {
        this.frameRotations.set(
            environment,
            rotation,
            this.viewsOf(environment)
        );
    }

    /**
     * Returns every view of `environment` to its base orientation, forgets
     * the frame and notifies the frame's subscribers. The zoom and the plane
     * intersection point stay as they are. Does nothing while `environment`
     * holds no frame, so views whose frame is owned elsewhere are never
     * re-oriented.
     */
    public clearFrameRotation(environment: Environment): void {
        this.frameRotations.clear(environment, this.viewsOf(environment));
    }

    /**
     * Fires after the frame of `environment` is set or reset.
     * @returns The unsubscribe.
     */
    public subscribeFrameRotation(
        environment: Environment,
        listener: () => void
    ): () => void {
        return this.frameRotations.subscribe(environment, listener);
    }

    /**
     * Fires after a view of `environment` that this manager holds changes
     * which image of a fused pair it draws: the primary, the secondary or
     * the blend. A view joining or leaving is signalled by {@link viewsFor}
     * instead. The same change of any held view is also dispatched on the
     * event hub as `fusedImageDisplayChanged`.
     * @returns The unsubscribe.
     */
    public subscribeFusedImageDisplay(
        environment: Environment,
        listener: () => void
    ): () => void {
        return this.fusedImageDisplayListeners.subscribe(environment, listener);
    }

    /**
     * The views of one Environment as a live handle: {@link viewsOf} on
     * demand, plus the signal that the set changed.
     */
    public viewsFor(environment: Environment): EnvironmentViews {
        return this.liveViews(environment, () => this.viewsOf(environment));
    }

    /**
     * The view of `environment` last pressed or wheeled on; its first view when
     * the last one used shows another Environment.
     */
    public lastUsedViewOf(environment: Environment): PlaneView | undefined {
        return this._lastUsedView?.environment === environment
            ? this._lastUsedView
            : this.viewsOf(environment)[0];
    }

    /** {@link viewsFor} over every Environment. */
    public everyView(): EnvironmentViews {
        return this.liveViews(null, () => [...this.allViews()]);
    }

    /**
     * A live handle on a set of views: `read` on demand, plus the signal
     * that the set under `key` changed.
     */
    private liveViews(
        key: Environment | null,
        read: () => readonly PlaneView[]
    ): EnvironmentViews {
        return {
            get current() {
                return read();
            },
            subscribe: (listener) =>
                this.viewListeners.subscribe(key, listener),
        };
    }

    /**
     * The environment whose primary image is the series `seriesInstanceUid`,
     * first in registration order; undefined when no view shows it. A fused
     * secondary image does not count: its views draw another series' frame.
     */
    public environmentShowingSeries(
        seriesInstanceUid: string
    ): Environment | undefined {
        return this.allViews().find(
            (view) =>
                view.environment.image?.parent.seriesUid === seriesInstanceUid
        )?.environment;
    }

    /** The views that currently show `environment`. */
    public viewsOf(environment: Environment): readonly PlaneView[] {
        const views: PlaneView[] = [];
        for (const view of this.allViews()) {
            if (view.environment === environment) {
                views.push(view);
            }
        }
        return views;
    }

    /**
     * Removes a grid tile's view. The Environment's frame stays with the
     * Environment: a view that joins it later is turned to the frame, so a
     * row whose tiles all leave and rejoin (a maximized tile, a plane cycle
     * of every tile) shows the same planes after as before. A placement
     * draft bound to the view is cancelled with it.
     */
    public removeGridView(view: PlaneView) {
        this.removeViewFrom(this._gridViews, view);
    }

    /**
     * Wires the source of the live Environments. A registration write re-transforms each of them that shares the
     * registration, and a reset of every view resets each turned one,
     * whether or not a view shows it. Read on each use, so an Environment
     * released from the source is never touched again.
     */
    public setResidentEnvironments(
        residents: () => ReadonlyArray<Environment>
    ): void {
        this.residentEnvironments = residents;
    }

    /**
     * Re-transforms the fused secondary image and the aligned images of
     * every other live Environment that holds `registration`, shown by a
     * view or not, and redraws the structures of every view that draws
     * another Environment's set. One registration object can back
     * the fusion of several grid rows (the pair read from either side), and
     * a matrix write on one of them must show on all of them; the writing
     * Environment re-transforms itself.
     */
    public retransformFusionsSharing(
        registration: RigidRegistration,
        writer: Environment
    ): void {
        for (const environment of this.residentEnvironments()) {
            if (
                environment !== writer &&
                (environment.registration === registration ||
                    environment.alignsThrough(registration))
            ) {
                environment.updateSecondaryTransformation();
            }
        }
        // A view drawing another Environment's structures places them
        // through a registration it reads on each draw.
        for (const view of this.allViews()) {
            if (view.structureProjection) {
                this.invalidationScheduler.markDirty(
                    view,
                    LayerBit.CONTOUR_LAYER_BIT
                );
            }
        }
    }

    /** Whether any grid-tile views are registered (gates contouring tools). */
    public static get gridViewsRegistered(): boolean {
        return (ViewManager.manager?._gridViews.length ?? 0) > 0;
    }

    /**
     * Forget the hover-tracked active view and held presses. Hosts call this
     * when attaching or detaching an input surface so neither a key nor a
     * release acts on a view of the previous surface.
     */
    public clearActiveView(): void {
        this._activeView = undefined;
        this._lastUsedView = undefined;
        this.buttonsPressedOnView.clear();
    }

    /**
     * Removes `view` from `list` and deletes it. An action of the armed tool
     * bound to the view is ended first
     * ({@link ToolManager.cancelActiveToolActionIn}): no view shows it any
     * more.
     * @returns Whether `list` held `view`.
     */
    private removeViewFrom(list: PlaneView[], view: PlaneView): boolean {
        const index = list.findIndex((v) => v === view);
        if (index < 0) {
            return false;
        }
        if (this._activeView === list[index]) {
            // A keydown after removal must not target the dead view.
            this._activeView = undefined;
        }
        if (this._lastUsedView === list[index]) {
            this._lastUsedView = undefined;
        }
        if (this.toolManager.getActiveToolDrawingView() === view) {
            // An action goes with its view: no tile shows it any more, and
            // left in place it would block slice scroll, pan and every press
            // on the views that remain. Ended before the pending repaint is
            // dropped, so the repaint it asks for never reaches this view.
            this.toolManager.cancelActiveToolActionIn(view);
        }
        // Drop the pending repaint before the GL resources go away.
        this.invalidationScheduler.discard(view);
        view.fusedImageDisplayChanged.unsubscribe(
            this.onFusedImageDisplayChanged
        );
        list[index].delete();
        list.splice(index, 1);
        this.notifyViewsChanged(view.environment);
        return true;
    }

    /**
     * Tells the subscribers to `environment`'s views, and every-view
     * subscribers, that the set changed.
     */
    private notifyViewsChanged(environment: Environment): void {
        this.viewListeners.notify(environment);
        this.viewListeners.notify(null);
    }

    /**
     * Implementation of {@link ViewInvalidator.invalidate}.\
     * Marks the {@link layers} dirty in every view whose viewport can show
     * {@link volume} — views that provably cannot are culled, and a missing or
     * malformed volume marks everything — and schedules a single repaint for the
     * next animation frame.
     *
     * Culling happens here rather than in the flush on purpose: {@link volume} is
     * live data (an {@link EnvironmentObject}'s bounding box keeps moving), so it
     * has to be evaluated when the caller reports the change, not a frame later.
     * What is deferred is only the repaint; a burst of invalidations inside one
     * frame therefore produces one {@link PlaneView.updateView} per affected view,
     * with the union of the masks it was marked dirty with.
     *
     * TODO: an invalidation reaches every environment's views, so a mutation
     * in one environment repaints the others' views too, gated only by
     * bounding-box culling. Scoping an invalidation to its own environment's
     * views would change what repaints.
     * @param layers Bitflag of the {@link Layer Layers} that changed.
     * @param volume Region the change touched. If omitted, all views are repainted.
     */
    public invalidate(layers: LayerBitMask, volume?: InvalidationVolume): void {
        const cullingVolume = resolveInvalidationVolume(volume);
        for (const view of this.allViews()) {
            // A view already scheduled to repaint these layers cannot be
            // affected by the verdict — either way, marking it leaves the union
            // unchanged — so the verdict is not worth computing. Culling is a
            // box-vs-frustum intersection, and callers raise invalidations in
            // bursts (one per ROI on a bulk property change), so this bounds the
            // cost at one intersection per view per frame.
            if (this.invalidationScheduler.isDirtyWith(view, layers)) {
                continue;
            }
            // A view drawing another Environment's structures lies in another
            // frame than the volume, so the volume cannot cull it.
            if (
                shouldUpdateView(
                    view.structureProjection ? undefined : cullingVolume,
                    view
                )
            ) {
                this.invalidationScheduler.markDirty(view, layers);
            }
        }
    }

    /** How many grid-tile views are registered. */
    public get gridViewCount(): number {
        return this._gridViews.length;
    }

    /**
     * Run any repaint {@link invalidate} has scheduled immediately, instead of on
     * the next animation frame, and cancel that frame.
     *
     * Meant for callers that must not proceed until the pending repaint has been
     * issued — and for tests, which would otherwise have to pump an animation
     * frame to observe an invalidation.
     *
     * **Not required by the viewport-screenshot PHI gate**, and harmful to it.
     * Because repaints are coalesced, visualised state can change up to a frame
     * before a frame carrying it is published on the canvas. No consumer of
     * {@link TextureView.canvasFrameId} / {@link TextureView.canvasFrameSources}
     * is exposed by that gap:
     *
     * - `viewportBurnedInStatus` / `viewportImagesAwaitingReview` describe the
     *   frame *published on the canvas*, not the live environment, and
     *   {@link TextureView.renderLayers} snapshots the composited sources and
     *   commits them with the frame id inside the same async transfer. The frame
     *   they describe is the one the user is looking at, so a pending repaint
     *   leaves them judging the old, possibly burned-in image until the new one
     *   is actually on screen — the fail-safe direction.
     * - `downloadViewportScreenshot` binds its bytes to one frame by requiring
     *   {@link TextureView.canvasFrameId} not to move across the capture, and
     *   `useScreenshotCapture` binds a pending Unknown-confirmation to the frame
     *   id it was opened against. Both are id comparisons, so a later publish is
     *   caught however long it takes to arrive.
     *
     * Flushing before a capture cannot help: the repaint publishes through an
     * async `createImageBitmap`, so no synchronous call makes the canvas current.
     * It would guarantee a frame-id bump mid-capture, turning a sound capture
     * into a spurious refusal.
     */
    public flushInvalidations(): void {
        this.invalidationScheduler.flush();
    }

    /**
     * Method to get the {@link TextureView} that is located at the provided {@link coordinates}.\
     * Undefined if none of the views overlaps with those coordinates.
     * @param coordinates PixelCoordinates that the {@link TextureView} should cover.
     * @returns The {@link TextureView} that covers the {@link coordinates}.\
     * Undefined if none of the views covers those coordinates.
     */
    private getView(coordinates: ReadonlyVec2): PlaneView | undefined {
        for (const view of this.allViews()) {
            if (view.canvas) {
                const rect = view.canvas.getBoundingClientRect();
                if (
                    rect.left <= coordinates[0] &&
                    rect.right >= coordinates[0] &&
                    ((rect.bottom <= coordinates[1] &&
                        rect.top >= coordinates[1]) ||
                        (rect.top <= coordinates[1] &&
                            rect.bottom >= coordinates[1]))
                ) {
                    return view;
                }
            }
        }
        return undefined;
    }

    /**
     * Whether an event within an element excluded from view input is left to
     * it: a press, move, release, wheel, double-click or key. A move or release
     * still reaches the tools only for a button pressed on a view, so a drag
     * crossing the element follows the pointer, and a click on the element
     * cannot commit a contour under it. A move left to it drops the active
     * view.
     */
    private leavesToExcludedElement(event: UIEvent): boolean {
        if (
            !ViewManager.EXCLUDABLE_EVENTS.has(event.type) ||
            !isExcludedFromViewInput(event.target)
        ) {
            return false;
        }
        switch (event.type) {
            case 'mouseup':
                return !this.buttonsPressedOnView.has(
                    (event as MouseEvent).button
                );
            case 'mousemove':
                if (this.buttonsPressedOnView.size > 0) {
                    return false;
                }
                this._activeView = undefined;
                return true;
            default:
                return true;
        }
    }

    /**
     * Method that will receive and propagate user-triggered {@link UIEvent events}.\
     * It first distinguishes the event type,\
     * gathers additional data that is useful to handle that type of event.\
     * And then propagates the event to {@link ToolManager}.
     * @param event {@link Event} that was triggered by user interaction on an input surface and caught here.
     */
    public handleEvent(event: UIEvent) {
        if (event.type === 'mousedown') {
            // Supersedes an earlier press whose release never arrived.
            this.buttonsPressedOnView.delete((event as MouseEvent).button);
        } else if (event.type === 'mousemove') {
            // A button no longer held was let go outside the surface.
            const held = (event as MouseEvent).buttons;
            for (const button of this.buttonsPressedOnView) {
                if ((held & buttonsMaskOf(button)) === 0) {
                    this.buttonsPressedOnView.delete(button);
                }
            }
        }
        if (this.leavesToExcludedElement(event)) {
            return;
        }
        const keyboardEvent: KeyboardEvent = event as KeyboardEvent;
        switch (event.type) {
            case 'mousedown': {
                const mouseEvent = event as MouseEvent;
                this.toolManager.syncPanMetaKey(mouseEvent.metaKey);
                // prevent browser auto-scroll on middle mouse button
                if (mouseEvent.button === 1) {
                    event.preventDefault();
                }
                const coordinates = vec2.fromValues(
                    mouseEvent.clientX,
                    mouseEvent.clientY
                );
                const view = this.getView(coordinates);
                const modifierKeys =
                    ModifierKeys.getModifierKeys(keyboardEvent);
                if (view && event.target === view.canvas) {
                    this.buttonsPressedOnView.add(mouseEvent.button);
                    this._lastUsedView = view;
                    if (
                        this.toolManager.onMousePress(
                            mouseEvent.button,
                            modifierKeys,
                            coordinates,
                            view
                        )
                    ) {
                        return;
                    }
                }
                break;
            }
            case 'mouseenter': {
                this.toolManager.onMouseEnter();
                break;
            }
            case 'mousemove': {
                const mouseEvent = event as MouseEvent;
                this.toolManager.syncPanMetaKey(mouseEvent.metaKey);
                const coordinates = vec2.fromValues(
                    mouseEvent.clientX,
                    mouseEvent.clientY
                );
                const buttons = mouseEvent.buttons;
                const movement = vec2.fromValues(
                    mouseEvent.movementX,
                    mouseEvent.movementY
                );
                const view = this.getView(coordinates);
                if (view) {
                    if (view !== this._activeView) {
                        this._activeView = view;
                    }
                    if (
                        this.toolManager.onMouseMove(
                            coordinates,
                            buttons,
                            view,
                            movement
                        )
                    ) {
                        return;
                    }
                } else if (this._activeView) {
                    this._activeView = undefined;
                }
                break;
            }
            case 'mouseup': {
                const mouseEvent = event as MouseEvent;
                this.buttonsPressedOnView.delete(mouseEvent.button);
                const coordinates = vec2.fromValues(
                    mouseEvent.clientX,
                    mouseEvent.clientY
                );
                const view = this.getView(coordinates);
                if (
                    view &&
                    this.toolManager.onMouseRelease(
                        mouseEvent.button,
                        coordinates,
                        view
                    )
                ) {
                    return;
                }
                break;
            }
            case 'mouseleave':
                this.toolManager.onMouseLeave();
                break;
            case 'wheel': {
                const wheelEvent = event as WheelEvent;
                // With Shift held, browsers remap the wheel to the
                // horizontal axis (deltaY becomes 0, deltaX carries the
                // value), so fall back to deltaX — otherwise Shift+wheel
                // interactions (brush sizing) read as one direction only.
                const rawDelta = wheelEvent.deltaY || wheelEvent.deltaX;
                if (rawDelta === 0) {
                    break;
                }
                const delta = rawDelta < 0 ? 1 : -1;
                const coordinates = vec2.fromValues(
                    wheelEvent.clientX,
                    wheelEvent.clientY
                );
                const view = this.getView(coordinates);
                if (!view) {
                    // Cursor is not over a viewport canvas — allow native
                    // scroll (e.g. table panel) to work unimpeded.
                    break;
                }
                this._lastUsedView = view;
                event.preventDefault();
                if (keyboardEvent.ctrlKey) {
                    keyboardEvent.preventDefault();
                }
                const modifierKeys =
                    ModifierKeys.getModifierKeys(keyboardEvent);
                if (
                    this.toolManager.onMouseWheel(
                        delta,
                        modifierKeys,
                        coordinates,
                        view
                    )
                ) {
                    return;
                }
                break;
            }
            case 'keydown': {
                const modifierKeys =
                    ModifierKeys.getModifierKeys(keyboardEvent);
                const view = this.viewForKey(keyboardEvent.key);
                if (
                    view &&
                    this.toolManager.onKeyPress(
                        keyboardEvent.key,
                        modifierKeys,
                        view
                    )
                ) {
                    event.preventDefault();
                }
                break;
            }
            case 'keyup': {
                break;
            }
            case 'dblclick': {
                const mouseEvent = event as MouseEvent;
                const coordinates = vec2.fromValues(
                    mouseEvent.clientX,
                    mouseEvent.clientY
                );
                const view = this.getView(coordinates);
                if (view && this.toolManager.onDoubleClick(coordinates, view)) {
                    return;
                }
                break;
            }
            case 'contextmenu':
                event.preventDefault();
                break;
            default:
                console.warn(
                    'Received unrecognize event of type ' + event.type
                );
                break;
        }
    }

    /**
     * Transitional shim: routes an invalidation to the {@link manager} singleton for
     * callers that do not (yet) hold a {@link ViewInvalidator} reference.
     *
     * TODO: slated for removal. Every caller of this should instead be handed the
     * {@link ViewInvalidator} that owns its views; once they all are, this static — and
     * with it the "before the singleton existed" failure mode, which injected
     * invalidation cannot have — goes away.
     * @param layers Bitflag of the {@link Layer Layers} that changed.
     * @param volume Region the change touched. If omitted, all views are repainted.
     */
    public static invalidate(
        layers: LayerBitMask,
        volume?: InvalidationVolume
    ): void {
        if (ViewManager.manager) {
            ViewManager.manager.invalidate(layers, volume);
        } else {
            console.error(
                'View invalidation of layers ' +
                    layers +
                    ' before ViewManager singleton was initialized'
            );
        }
    }

    public static resetViewsForEnvironment(env: Environment): void {
        ViewManager.manager?.resetViewsForEnvironment(env);
    }

    public static clearFrameRotation(env: Environment): void {
        ViewManager.manager?.clearFrameRotation(env);
    }

    public static retransformFusionsSharing(
        registration: RigidRegistration,
        writer: Environment
    ): void {
        ViewManager.manager?.retransformFusionsSharing(registration, writer);
    }

    public static addEventListener(
        type: string,
        listener: EventListenerOrEventListenerObject,
        options?: boolean | AddEventListenerOptions | undefined
    ): void {
        if (ViewManager.manager) {
            ViewManager.manager.addEventListener(type, listener, options);
        } else {
            throw new Error(
                `Attempt to add event listener to ViewManager, while ViewManager has not been initialized`
            );
        }
    }

    public static getDicomProcessor(): DicomProcessor {
        if (ViewManager.manager) {
            return ViewManager.manager.dicomProcessor;
        }
        throw new Error('No ViewManager initialized yet');
    }
}
